import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { ListTodo, Zap, CheckCircle2, Circle, ChevronDown, Loader2, User, XCircle, ArrowRight, CalendarPlus, Mail, Send, Phone, Sparkles, FileText, FileSignature, Plus, UserPlus } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { FollowupOutcomeDialog } from './FollowupOutcomeDialog';
import { useAuth } from '../contexts/AuthContext';

const taskErrMsg = (e, fallback) => {
  const d = e.response?.data?.detail;
  if (d && typeof d === 'object' && d.code === 'stage_gate') return `Missing for ${d.target_label}: ${(d.missing || []).join(' · ')}`;
  if (d && typeof d === 'object' && d.message) return d.message;
  return typeof d === 'string' ? d : fallback;
};
// Contact-enrich tasks (Talu, 22.10.2026): anyone may do them or take them over
const isEnrichTask = (t) => ['olta_approved_enrich_contact', 'outreach_step_enrich'].includes(t?.template_id);

const fmtDue = (iso) => {
  if (!iso) return null;
  const days = (new Date(iso) - Date.now()) / 86400000;
  const d = new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  if (days < 0) return { text: `Overdue since ${d}`, cls: 'text-red-400' };
  if (days < 1) return { text: 'Due today', cls: 'text-amber-400' };
  return { text: `Due ${d}`, cls: 'text-muted-foreground' };
};

const CHANNELS = [
  { value: 'letter', label: 'Letter (post)', desc: 'Formal letter mailed to the owner', icon: Send },
  { value: 'email', label: 'Email', desc: 'Shorter, direct email outreach', icon: Mail },
  { value: 'other', label: 'Other', desc: 'Phone, LinkedIn, broker intro…', icon: Phone },
];
const channelMeta = (v) => CHANNELS.find(c => c.value === v);

export function DealTasks({ projectId, stage, onStageChanged }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [confirmTask, setConfirmTask] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [claiming, setClaiming] = useState(null);
  const [channelModal, setChannelModal] = useState(null); // {task} or {task: null} for chip edit
  const [outcomeTask, setOutcomeTask] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [members, setMembers] = useState([]);
  const [addForm, setAddForm] = useState({ title: '', due_date: '', assignee_id: '' });

  const load = useCallback(() => {
    api.getDealTasks(projectId).then(r => setData(r.data)).catch(() => {});
  }, [projectId]);

  useEffect(() => { load(); }, [load, stage]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener('outreach-flow-changed', h);
    return () => window.removeEventListener('outreach-flow-changed', h);
  }, [load]);

  const doComplete = async (task, channel) => {
    setCompleting(task.id);
    try {
      const r = await api.completeTask(task.id, channel ? { channel } : undefined);
      if (r.data.new_stage_label) {
        toast.success(`Task completed — deal moved to ${r.data.new_stage_label}`);
        if (onStageChanged) onStageChanged();
      } else {
        toast.success('Task completed');
      }
      load();
      window.dispatchEvent(new Event('outreach-tasks-changed'));
    } catch (e) {
      toast.error(taskErrMsg(e, 'Could not complete task'));
    } finally {
      setCompleting(null);
      setConfirmTask(null);
    }
  };

  const doAgenda = async (task) => {
    setCompleting(task.id);
    try {
      await api.addToAgenda(projectId);
      toast.success('Added to the team meeting agenda — task completed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add to agenda');
    } finally {
      setCompleting(null);
    }
  };

  const doClaim = async (task) => {
    setClaiming(task.id);
    try {
      await api.claimTask(task.id);
      toast.success(`Aufgabe übernommen${task.assignee_name ? ` — ${task.assignee_name.split(' ')[0]} wurde informiert` : ''}`);
      load();
      window.dispatchEvent(new Event('outreach-tasks-changed'));
    } catch (e) {
      toast.error(taskErrMsg(e, 'Übernehmen fehlgeschlagen'));
    } finally {
      setClaiming(null);
    }
  };

  const chooseChannel = async (value) => {
    const task = channelModal?.task;
    setChannelModal(null);
    if (task) {
      await doComplete(task, value);
      return;
    }
    try {
      await api.setOutreachChannel(projectId, value);
      toast.success('Contact channel updated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not set channel');
    }
  };

  const doDraft = async () => {
    setGenerating(true);
    try {
      const ch = data?.outreach_channel === 'email' ? 'email' : 'letter';
      await api.generateLetter(projectId, ch);
      toast.success(ch === 'email' ? 'Email draft created' : 'Letter draft created');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Draft generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handlePrimaryClick = (t) => {
    const isSeqCall = t.seq_channel === 'call' || ((t.template_id || '').startsWith('seq:') && (t.title || '').startsWith('Call '));
    if (t.source === 'outreach_followup' || isSeqCall) return setOutcomeTask(t);
    if (t.auto_complete_on === 'agenda_added') return doAgenda(t);
    if (t.event === 'pull_to_outreach' && !data.outreach_channel) return setChannelModal({ task: t });
    if (t.is_primary && t.advances_to) return setConfirmTask(t);
    doComplete(t);
  };

  const doCompleteOutcome = async (outcome, extra) => {
    const task = outcomeTask;
    setCompleting(task.id);
    try {
      await api.completeTask(task.id, outcome ? {
        followup_outcome: outcome,
        ...(extra?.category ? { followup_reason_category: extra.category } : {}),
        ...(extra?.reason ? { followup_reason_text: extra.reason } : {}),
      } : undefined);
      toast.success(outcome ? 'Call result saved — task completed' : 'Task completed');
      setOutcomeTask(null);
      load();
    } catch (e) {
      toast.error(taskErrMsg(e, 'Could not complete task'));
    } finally {
      setCompleting(null);
    }
  };

  if (!data) return null;

  const slaChip = () => {
    const t = data.timer;
    if (!t || t.days_left == null) return null;
    const d = t.days_left;
    const totalH = Math.round(Math.abs(d) * 24);
    const dd = Math.floor(totalH / 24); const hh = totalH % 24;
    const str = dd > 0 ? `${dd}d ${hh}h` : `${hh}h`;
    const cls = d < 0 ? 'border-red-500/40 bg-red-500/10 text-red-400'
      : d < 1 ? 'border-amber-500/40 bg-amber-500/10 text-amber-400'
      : 'border-border bg-secondary text-muted-foreground';
    const label = t.consequence === 'unclaim' ? 'auto-release' : t.consequence === 'escalate' ? 'escalates to Talu' : 'reminder';
    return (
      <span className={`text-[10px] px-2 py-0.5 rounded border font-mono ${cls}`} data-testid="deal-sla-chip"
        title={`Hard SLA (${t.sla_days} business days from stage entry) — consequence: ${label}${t.escalated ? ' · already escalated' : ''}`}>
        SLA {d < 0 ? `${str} overdue` : `${str} left`}{t.escalated ? ' 🚨' : ''}
      </span>
    );
  };

  const openAdd = async () => {
    setAddForm({ title: '', due_date: '', assignee_id: '' });
    setAddOpen(true);
    if (members.length === 0) {
      try { setMembers((await api.getTeamMembers()).data || []); } catch { /* dropdown stays minimal */ }
    }
  };

  const doAddTask = async () => {
    if (!addForm.title.trim()) { toast.error('Titel fehlt'); return; }
    setAdding(true);
    try {
      await api.createDealTask(projectId, addForm);
      toast.success('Task erstellt');
      setAddOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Task konnte nicht erstellt werden');
    } finally {
      setAdding(false);
    }
  };

  const cm = channelMeta(data.outreach_channel);

  return (
    <div className="bg-card border border-border rounded-sm overflow-hidden mb-6" data-testid="deal-tasks-panel">
      <div className="flex items-center gap-3 p-4 border-b border-border flex-wrap">
        <div className="w-8 h-8 rounded-sm bg-blue-500/20 flex items-center justify-center">
          <ListTodo className="w-4 h-4 text-blue-400" />
        </div>
        <span className="font-heading text-base text-foreground">Next Steps</span>
        {data.open.length > 0 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-600/15 border border-blue-600/30 text-blue-400 font-mono">
            {data.open.length} open
          </span>
        )}
        {cm && (
          <button onClick={() => setChannelModal({ task: null })} data-testid="deal-channel-chip"
            title="Contact channel — click to change"
            className="text-[10px] px-2 py-0.5 rounded border border-purple-500/30 bg-purple-500/10 text-purple-400 font-mono flex items-center gap-1 hover:border-purple-400/60 transition-colors">
            <cm.icon className="w-3 h-3" /> {cm.label}
          </button>
        )}
        {data.draft && (
          <span className="text-[10px] px-2 py-0.5 rounded border border-border bg-secondary text-muted-foreground font-mono flex items-center gap-1" data-testid="deal-draft-chip">
            <FileText className="w-3 h-3" /> {data.draft.letter_type === 'email' ? 'Email' : 'Letter'} · {data.draft.status}
          </span>
        )}
        {data.touch && (
          <span className="text-[10px] px-2 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-400 font-mono" data-testid="deal-touch-chip"
            title={data.touch.exhausted ? 'No response after 3 touches — pass proposal created' : 'Outreach touches without a response (follow-up every 10 business days)'}>
            Touch {data.touch.count}/3{data.touch.exhausted ? ' · no response' : ''}
          </span>
        )}
        <span className="ml-auto flex items-center gap-2">
          {slaChip()}
          <Button size="sm" variant="outline" onClick={openAdd} data-testid="deal-add-task-btn"
            className="h-7 px-2 text-[11px] rounded-sm border-border hover:border-blue-500/50 hover:text-blue-400">
            <Plus className="w-3 h-3 mr-1" /> Task
          </Button>
        </span>
      </div>

      <div className="p-4 space-y-2">
        {data.open.length === 0 ? (
          <p className="text-xs text-muted-foreground flex items-center gap-2" data-testid="deal-tasks-empty">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" /> No open tasks for this stage.
          </p>
        ) : data.open.map(t => {
          const due = fmtDue(t.due_at);
          const isAgendaTask = t.auto_complete_on === 'agenda_added';
          const isDraftTask = t.template_id === 'outreach_step_draft';
          const isApproveTask = (t.template_id || '').startsWith('outreach_step_') && t.template_id !== 'outreach_step_draft';
          return (
            <div key={t.id} className="flex items-start gap-3 p-3 rounded-sm border border-border bg-secondary/30" data-testid={`task-row-${t.id}`}>
              <button
                onClick={() => handlePrimaryClick(t)}
                disabled={completing === t.id}
                className="mt-0.5 text-muted-foreground hover:text-emerald-400 transition-colors shrink-0"
                title={isAgendaTask ? 'Add to the team meeting agenda' : t.advances_to ? `Complete — moves deal to ${t.advances_to}` : 'Complete task'}
                data-testid={`task-complete-btn-${t.id}`}>
                {completing === t.id ? <Loader2 className="w-5 h-5 animate-spin" /> : isAgendaTask ? <CalendarPlus className="w-5 h-5" strokeWidth={1.5} /> : <Circle className="w-5 h-5" strokeWidth={1.5} />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm text-foreground font-medium">{t.title}</p>
                  {t.is_primary && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-400 font-mono uppercase flex items-center gap-1">
                      <Zap className="w-2.5 h-2.5" /> Primary
                    </span>
                  )}
                  {t.advances_to && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded border border-blue-500/30 bg-blue-500/10 text-blue-400 font-mono flex items-center gap-1">
                      <ArrowRight className="w-2.5 h-2.5" /> {t.advances_to}
                    </span>
                  )}
                </div>
                {t.description && <p className="text-[11px] text-muted-foreground mt-0.5">{t.description}</p>}
                {t.template_id === 'first_call_notes' && (
                  <button
                    onClick={() => document.querySelector('[data-testid="nda-card"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                    data-testid="task-goto-nda-btn"
                    className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-teal-400 hover:text-teal-300 border border-teal-600/40 bg-teal-600/10 rounded-sm px-2 py-1 transition-colors">
                    <FileSignature className="w-3 h-3" /> Open NDA card
                  </button>
                )}
                {(t.gate_checks || []).length > 0 && (
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap" data-testid={`task-gate-checks-${t.id}`}>
                    {t.gate_checks.map((c, i) => (
                      <span key={i} data-testid={`task-gate-check-${c.done ? 'done' : 'open'}`}
                        className={`text-[10px] px-1.5 py-0.5 rounded border font-mono flex items-center gap-1 ${
                          c.done
                            ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400'
                            : 'border-border bg-secondary/40 text-muted-foreground'}`}
                        title={c.done ? 'Requirement met' : 'Still needed to advance the stage'}>
                        {c.done ? <CheckCircle2 className="w-3 h-3" /> : <Circle className="w-3 h-3" />}
                        {c.label}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  {due && <span className={`text-[10px] font-mono ${due.cls}`}>{due.text}</span>}
                  <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                    <User className="w-3 h-3" /> {t.assignee_name || 'Unassigned'}
                    {t.claimed_from_name && <span className="text-muted-foreground/60" data-testid={`task-claimed-from-${t.id}`}>(übernommen von {t.claimed_from_name.split(' ')[0]})</span>}
                  </span>
                  {isEnrichTask(t) && user && t.assignee_id !== user.id && (
                    <Button size="sm" variant="outline" onClick={() => doClaim(t)} disabled={claiming === t.id}
                      title="Aufgabe übernehmen — der bisherige Zuständige wird informiert"
                      className="h-6 px-2 text-[10px] rounded-sm border-border hover:border-sky-500/50 hover:text-sky-300"
                      data-testid={`task-claim-btn-${t.id}`}>
                      {claiming === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><UserPlus className="w-3 h-3 mr-1" /> Übernehmen</>}
                    </Button>
                  )}
                  {isAgendaTask && (
                    <Button size="sm" variant="outline" onClick={() => doAgenda(t)} disabled={completing === t.id}
                      className="h-6 px-2 text-[10px] rounded-sm border-border hover:border-blue-500/50 hover:text-blue-400"
                      data-testid={`task-agenda-btn-${t.id}`}>
                      <CalendarPlus className="w-3 h-3 mr-1" /> Add to Agenda
                    </Button>
                  )}
                  {isDraftTask && !data.draft && (
                    <Button size="sm" variant="outline" onClick={doDraft} disabled={generating}
                      className="h-6 px-2 text-[10px] rounded-sm border-border hover:border-purple-500/50 hover:text-purple-400"
                      data-testid={`task-draft-btn-${t.id}`}>
                      {generating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                      {generating ? 'Drafting…' : 'Draft with AI'}
                    </Button>
                  )}
                  {(isDraftTask || isApproveTask) && data.draft && (
                    <Button size="sm" variant="outline" onClick={() => navigate('/outreach')}
                      className="h-6 px-2 text-[10px] rounded-sm border-border hover:border-blue-500/50 hover:text-blue-400"
                      data-testid={`task-view-draft-btn-${t.id}`}>
                      <FileText className="w-3 h-3 mr-1" /> View draft
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {data.recent.length > 0 && (
          <div>
            <button onClick={() => setShowHistory(h => !h)} data-testid="task-history-toggle"
              className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors mt-1">
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showHistory ? '' : '-rotate-90'}`} />
              Recent history ({data.recent.length})
            </button>
            {showHistory && (
              <div className="mt-2 space-y-1.5">
                {data.recent.map(t => (
                  <div key={t.id} className="flex items-center gap-2 px-3 py-1.5 rounded-sm bg-secondary/20 text-[11px]" data-testid={`task-history-${t.id}`}>
                    {t.status === 'done'
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                      : <XCircle className="w-3.5 h-3.5 text-muted-foreground/50 shrink-0" />}
                    <span className={`flex-1 min-w-0 truncate ${t.status === 'done' ? 'text-foreground/70 line-through' : 'text-muted-foreground/60'}`}>{t.title}</span>
                    <span className="text-muted-foreground/50 font-mono shrink-0">
                      {t.status === 'done'
                        ? `by ${t.completed_by_name || 'System'}`
                        : t.cancelled_reason === 'stage_exited' ? 'stage changed' : 'cancelled'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Channel gate modal */}
      <Dialog open={!!channelModal} onOpenChange={(o) => !o && setChannelModal(null)}>
        <DialogContent className="bg-card border-border rounded-sm max-w-sm" data-testid="channel-gate-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
              <Send className="w-5 h-5 text-purple-400" /> Choose the contact channel
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-1">
            {channelModal?.task
              ? 'How will the owner be contacted? This starts the outreach and moves the deal to Outreach WIP.'
              : 'How will the owner be contacted? This determines the AI draft type.'}
          </p>
          <div className="space-y-2 mt-1">
            {CHANNELS.map(c => (
              <button key={c.value} onClick={() => chooseChannel(c.value)} data-testid={`channel-option-${c.value}`}
                className={`w-full flex items-center gap-3 p-3 rounded-sm border text-left transition-colors ${
                  data.outreach_channel === c.value
                    ? 'border-purple-500/50 bg-purple-500/10'
                    : 'border-border bg-secondary/30 hover:border-purple-500/40'
                }`}>
                <c.icon className="w-4 h-4 text-purple-400 shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-foreground font-medium">{c.label}</span>
                  <span className="block text-[11px] text-muted-foreground">{c.desc}</span>
                </span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Complete & advance confirm */}
      <Dialog open={!!confirmTask} onOpenChange={(o) => !o && setConfirmTask(null)}>
        <DialogContent className="bg-card border-border rounded-sm max-w-sm" data-testid="task-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-400" /> Complete & advance?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Completing <span className="text-foreground font-medium">"{confirmTask?.title}"</span> will move the deal to{' '}
            <span className="text-blue-400 font-medium">{confirmTask?.advances_to}</span>.
          </p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" onClick={() => setConfirmTask(null)} className="text-xs h-8 px-3">Cancel</Button>
            <Button onClick={() => doComplete(confirmTask)} disabled={!!completing} data-testid="task-confirm-btn"
              className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm font-medium text-xs h-8 px-3">
              {completing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Complete & advance'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-sm" data-testid="deal-add-task-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base text-foreground flex items-center gap-2">
              <Plus className="w-4 h-4 text-blue-400" /> Neuer Task
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Titel</p>
              <Input value={addForm.title} onChange={e => setAddForm(f => ({ ...f, title: e.target.value }))}
                placeholder="z.B. Unterlagen nachfassen" data-testid="deal-add-task-title"
                className="bg-secondary border-border rounded-sm text-xs h-8" autoFocus />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Fällig am</p>
              <Input type="date" value={addForm.due_date} onChange={e => setAddForm(f => ({ ...f, due_date: e.target.value }))}
                data-testid="deal-add-task-due" className="bg-secondary border-border rounded-sm text-xs h-8" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Verantwortlich</p>
              <select value={addForm.assignee_id} onChange={e => setAddForm(f => ({ ...f, assignee_id: e.target.value }))}
                data-testid="deal-add-task-assignee"
                className="w-full bg-secondary border border-border rounded-sm text-xs h-8 px-2 text-foreground">
                <option value="">Ich selbst</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setAddOpen(false)} className="text-xs h-8 px-3">Abbrechen</Button>
              <Button onClick={doAddTask} disabled={adding} data-testid="deal-add-task-save"
                className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-8 px-3">
                {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Task anlegen'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <FollowupOutcomeDialog task={outcomeTask} busy={!!completing}
        onSelect={doCompleteOutcome} onSkip={() => doCompleteOutcome(null)} onClose={() => setOutcomeTask(null)} />
    </div>
  );
}
